import { useFeatures } from '@xingternal/hub';

import { JDPModuleName, JobPostingModuleConfig } from '@/components/job-posting/types';
import { JobPostingVisibleJobFragment } from '@/graphql/generated';
import { useLoginStates } from '@/hooks';

const supportedSummaryLanguages = ['de', 'en'];

export const useModulesConfig = (
  job: JobPostingVisibleJobFragment
): {
  modules: JobPostingModuleConfig[];
  showModule: (moduleName: JDPModuleName) => boolean;
} => {
  const { company } = job.companyInfo;
  const isAutoGenerated = !!company?.autogenerated;
  const { isLoggedOut } = useLoginStates();
  const hasCompanyLink = !!company?.links?.public;
  const { data } = useFeatures();
  const isPremium = data?.isPremium;
  const kununuData = company?.kununuData;
  const showKununu = kununuData?.mappedBenefits || kununuData?.culture || (kununuData?.ratingCount ?? 0) > 0;
  const showSummary = !!job.summary && (!job.language || supportedSummaryLanguages.includes(job.language));

  const modules: JobPostingModuleConfig[] = [
    { name: 'Header', status: 'VISIBLE' },
    { name: 'Summary', status: showSummary ? 'VISIBLE' : 'HIDDEN' },
    { name: 'Description', status: 'VISIBLE' },
    { name: 'Salary', status: job.salary ? 'VISIBLE' : 'HIDDEN' },
    { name: 'Company', status: !!hasCompanyLink && !isAutoGenerated ? 'VISIBLE' : 'HIDDEN' },
    { name: 'HRContact', status: !!job?.contact && !isLoggedOut && isPremium ? 'VISIBLE' : 'HIDDEN' },
    { name: 'Culture', status: showKununu ? 'VISIBLE' : 'HIDDEN' },
    { name: 'Contacts', status: !!company && !isLoggedOut ? 'LOADING' : 'HIDDEN' },
    { name: 'SearchAlert', status: 'VISIBLE' },
    { name: 'Video', status: job.video ? 'VISIBLE' : 'HIDDEN' },
    { name: 'SimilarJobs', status: 'LOADING' },
  ];
  const showModule = (moduleName: JDPModuleName): boolean =>
    modules?.find((module) => module.name === moduleName)?.status !== 'HIDDEN';

  return {
    modules,
    showModule,
  };
};
